import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Avatar,
  Tooltip,
  Divider
} from "@mui/material";
import { KeyboardArrowDown as ArrowDownIcon } from "@mui/icons-material";
import { colors, fonts, spacing, borderRadius } from "../../constants";
import { getUserInitials } from "../../utils/mainLayoutUtils";
import { getRoleDisplayName } from "../../utils/roleUtils";
import PostSelector from "./PostSelector";

/**
 * PageHeader Component
 * AppBar with page title, icon, and user profile section
 */
const PageHeader = ({ pageTitle, pageIcon, user, anchorEl, onMenuClick }) => {
  const displayName = user?.full_name || user?.name;

  // Resolve designation based on login type
  const isSSOLogin = !!localStorage.getItem("ssoToken");
  let designation = "";
  if (isSSOLogin) {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      designation =
        userData?.designm ||
        userData?.designation ||
        userData?.rolenm ||
        user?.designation ||
        getRoleDisplayName(user?.role);
    } catch {
      designation = user?.designation || getRoleDisplayName(user?.role);
    }
  } else {
    designation = user?.designation || getRoleDisplayName(user?.role);
  }
  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: colors.backgroundWhite,
        borderBottom: `1px solid ${colors.borderLight}`,
        color: colors.textPrimaryDark,
        boxShadow:
          "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.05)",
        zIndex: 1100
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: spacing.sm, sm: spacing.md },
          minHeight: "48px !important",
          height: "48px",
          maxHeight: "48px",
          py: 0
        }}
      >
        {/* Left Section - Page Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: spacing.xs }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: colors.textPrimaryDark,
              mr: "2px"
            }}
          >
            {pageIcon}
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: fonts.weightBold,
              fontSize: fonts.fontSizeLg,
              color: colors.textPrimaryDark,
              lineHeight: 1.2,
              margin: 0,
              display: "flex",
              alignItems: "center",
              letterSpacing: fonts.letterSpacingTight
            }}
          >
            {pageTitle}
          </Typography>
        </Box>

        {/* Right Section - Actions and User Profile */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {/* Post Selector - renders nothing for single-post/no-post users */}
          <PostSelector />

          {/* Divider */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: 24,
              mx: spacing.xs,
              borderColor: colors.borderLight
            }}
          />

          {/* User Profile Section */}
          <Tooltip title="Account Menu" arrow placement="bottom">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: spacing.xs,
                cursor: "pointer",
                px: spacing.xs,
                py: "4px",
                borderRadius: borderRadius.sm,
                transition: "all 0.15s ease",
                border: `1px solid transparent`,
                "&:hover": {
                  backgroundColor: colors.backgroundSoft,
                  borderColor: colors.borderDefault
                }
              }}
              onClick={onMenuClick}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  background: colors.avatarGradient,
                  color: colors.avatarText,
                  fontSize: fonts.fontSizeSm,
                  fontWeight: fonts.weightBold,
                  border: "none",
                  boxShadow: `0 2px 8px ${colors.avatarShadow}`,
                  transition: "all 0.15s ease"
                }}
              >
                {getUserInitials(user)}
              </Avatar>
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: fonts.weightSemiBold,
                    fontSize: fonts.fontSizeSm,
                    color: colors.textPrimaryDark,
                    lineHeight: 1.2,
                    margin: 0,
                    letterSpacing: fonts.letterSpacingTight
                  }}
                >
                  {displayName || "User"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textSecondary,
                    fontSize: fonts.fontSizeXs,
                    lineHeight: 1.2,
                    margin: 0
                  }}
                >
                  {designation}
                </Typography>
              </Box>
              <ArrowDownIcon
                sx={{
                  color: colors.textSecondary,
                  fontSize: "16px",
                  transition: "transform 0.15s ease",
                  transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)",
                  display: { xs: "none", sm: "block" }
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default PageHeader;
